import { useCallback, useEffect, useRef, useState } from "react";
import { fileRecordVersionKey } from "../../../lib/fileRecordVersion";
import {
  islandOnlyChanges,
  presignedLinkRenewals,
  splitHtmlIslands,
  type HtmlIslandSplit,
} from "../../../lib/htmlBlockLinks";
import { computeHtmlSrcDocFingerprint } from "../../../lib/htmlIframeSrcDoc";
import { useFileRecordText } from "../../../lib/useFileRecordText";

export interface HtmlCardDisplayDoc {
  /** React key for the iframe — changing it is the ONLY thing that remounts. */
  mountKey: string;
  srcDoc: string;
}

interface PendingVersion {
  fingerprint: string;
  original: string;
}

interface DeliveredVersion {
  original: string;
  /** After `transformHtml`: what the frame's links currently are. */
  transformed: string;
  /** The transform that produced it; a new identity means new links. */
  transformedBy: UseHtmlCardSrcDocParams["transformHtml"];
  optionsSalt: string;
  split: HtmlIslandSplit | null;
}

export interface UseHtmlCardSrcDocParams {
  /** Presigned URL of the HTML body. Rotation alone never refetches — the
   *  body is cached by file version ({@link fileRecordVersionKey}). */
  url: string | null | undefined;
  path: string;
  content_hash?: string;
  updated_at?: string;
  /**
   * Optional async rewrite applied to the fetched HTML before preparing —
   * used to swap embedded public file URLs for presigned ones. Its identity
   * changes when an embedded link is renewed: the delivered body is
   * transformed again and the new links are swapped in place.
   */
  transformHtml?: (html: string) => Promise<string> | string;
  /**
   * Render-option identity folded into the fingerprint (theme, transparency).
   * A salt change forces a fresh document; an unchanged salt + unchanged body
   * is a guaranteed no-op.
   */
  optionsSalt: string;
  /** Turn the transformed body into a srcDoc string (prepareSrcDocHtml with
   *  the card's options). Must be memoized on the same identity the salt
   *  captures — its identity changing re-runs the delivery effect. */
  prepare: (transformed: string) => string;
  /** While true, refetched content is stashed instead of committed so the
   *  iframe never remounts mid edit-session; the stash is reconciled when
   *  this flips back to false. */
  contentFrozen?: boolean;
  /** Embedded links renewed ahead of expiry, old → new, for the running
   *  document to swap in place. Without it the card re-prepares its srcDoc,
   *  which reloads the frame with the new links. */
  onLinksRenewed?: (map: Record<string, string>) => void;
  /**
   * A new version that differs from the displayed one only in island text:
   * the changed regions (transformed like the document), instead of a
   * remount. Without it every new version remounts.
   */
  onIslandsChanged?: (regions: Record<string, string>) => void;
}

export interface UseHtmlCardSrcDocResult {
  displayDoc: HtmlCardDisplayDoc | null;
}

/**
 * Shared text-cache → transform → fingerprint → prepare pipeline for the
 * canvas HTML cards (block and legacy thumb variants).
 *
 * Remount discipline: the fingerprint hashes the ORIGINAL (pre-transform)
 * body + `optionsSalt`, and an unchanged fingerprint performs ZERO state
 * updates — not "sets the same value". Rewriting the `srcdoc` attribute
 * reloads an iframe even when the React key is unchanged, so the equality
 * check must return before `setDisplayDoc` is ever called. Presigned URL
 * rotation therefore never restarts in-frame JS.
 *
 * Edit sessions hold `contentFrozen` while typing, saving, and for a short
 * settle window afterwards, so every refetch a save triggers lands in the
 * stash rather than reloading the frame under the user. On unfreeze the stash
 * is applied once. When the caller handles `onIslandsChanged`, a version
 * whose only difference is island text (the common case after an edit) is
 * applied in place and the frame never reloads.
 */
export function useHtmlCardSrcDoc({
  url,
  path,
  content_hash,
  updated_at,
  transformHtml,
  optionsSalt,
  prepare,
  contentFrozen = false,
  onLinksRenewed,
  onIslandsChanged,
}: UseHtmlCardSrcDocParams): UseHtmlCardSrcDocResult {
  const [displayDoc, setDisplayDoc] = useState<HtmlCardDisplayDoc | null>(null);

  const expectedFingerprintRef = useRef<string | null>(null);
  const pendingRef = useRef<PendingVersion | null>(null);
  const deliveredRef = useRef<DeliveredVersion | null>(null);
  // Bumped whenever earlier async work must be ignored.
  const generationRef = useRef(0);

  const versionKey = fileRecordVersionKey({ path, content_hash, updated_at });
  const { data: original } = useFileRecordText({ url, versionKey });

  const transformHtmlRef = useRef(transformHtml);
  transformHtmlRef.current = transformHtml;
  const onLinksRenewedRef = useRef(onLinksRenewed);
  onLinksRenewedRef.current = onLinksRenewed;
  const onIslandsChangedRef = useRef(onIslandsChanged);
  onIslandsChangedRef.current = onIslandsChanged;
  const prepareRef = useRef(prepare);
  prepareRef.current = prepare;
  const optionsSaltRef = useRef(optionsSalt);
  optionsSaltRef.current = optionsSalt;

  const contentFrozenRef = useRef(contentFrozen);
  // Whether a document has ever been committed to the card (see the freeze
  // branch below).
  const hasDeliveredRef = useRef(false);

  // Embedded links expire on their own clock. The rewrite hook observes the
  // rows behind them, so a renewal gives `transformHtml` a new identity:
  // transform the delivered body again and swap the links in place.
  useEffect(() => {
    const delivered = deliveredRef.current;
    if (!delivered || !transformHtml) return;
    if (delivered.transformedBy === transformHtml) return;
    delivered.transformedBy = transformHtml;
    const generation = generationRef.current;
    void Promise.resolve(transformHtml(delivered.original))
      .then((fresh) => {
        if (
          generation !== generationRef.current ||
          deliveredRef.current !== delivered ||
          fresh == null ||
          fresh === delivered.transformed
        ) {
          return;
        }
        const previous = delivered.transformed;
        delivered.transformed = fresh;
        const onRenewed = onLinksRenewedRef.current;
        if (onRenewed) {
          const map = presignedLinkRenewals(previous, fresh);
          if (map && Object.keys(map).length > 0) {
            onRenewed(map);
            return;
          }
          // The two documents don't pair, so there is nothing to swap in
          // place — and the frame is holding links nobody renewed. Reload it.
        }
        // Nothing to swap in place: rewriting `srcdoc` reloads the frame with
        // live links.
        const srcDoc = prepareRef.current(fresh);
        setDisplayDoc((shown) => (shown ? { ...shown, srcDoc } : shown));
      })
      .catch(() => {
        // Keep the links we have; the next renewal tries again.
      });
  }, [transformHtml]);

  const applyVersion = useCallback(
    async (body: string, fingerprint: string) => {
      const generation = ++generationRef.current;
      const transform = transformHtmlRef.current;
      const delivered = deliveredRef.current;

      // Only island text changed: hand the regions to the running document.
      // The parse runs here and nowhere else — once per new version.
      if (
        delivered &&
        onIslandsChangedRef.current &&
        delivered.optionsSalt === optionsSaltRef.current
      ) {
        delivered.split ??= splitHtmlIslands(delivered.original);
        const nextSplit = splitHtmlIslands(body);
        const changed = islandOnlyChanges(delivered.split, nextSplit);
        if (changed) {
          const regions: Record<string, string> = {};
          for (const [id, html] of Object.entries(changed)) {
            regions[id] = transform ? await transform(html) : html;
          }
          // The renewal path diffs `transformed` against a fresh transform
          // of `original`: leaving it on the previous version pairs two
          // different documents, and the links are never swapped.
          const transformedBody = transform ? await transform(body) : body;
          if (generation !== generationRef.current) return;
          expectedFingerprintRef.current = fingerprint;
          pendingRef.current = null;
          delivered.original = body;
          delivered.split = nextSplit;
          if (transformedBody != null) {
            delivered.transformed = transformedBody;
            delivered.transformedBy = transform;
          }
          if (Object.keys(regions).length > 0) {
            onIslandsChangedRef.current?.(regions);
          }
          return;
        }
      }

      const transformed = transform ? await transform(body) : body;
      if (generation !== generationRef.current || transformed == null) return;
      const srcDoc = prepareRef.current(transformed);
      expectedFingerprintRef.current = fingerprint;
      pendingRef.current = null;
      hasDeliveredRef.current = true;
      deliveredRef.current = {
        original: body,
        transformed,
        transformedBy: transform,
        optionsSalt: optionsSaltRef.current,
        split: null,
      };
      setDisplayDoc({ mountKey: fingerprint, srcDoc });
    },
    [],
  );

  useEffect(() => {
    if (original == null) return;
    const fingerprint =
      computeHtmlSrcDocFingerprint(original, undefined) + optionsSalt;
    // Same body + same options: zero state updates.
    if (fingerprint === expectedFingerprintRef.current) return;

    // Freezing protects a MOUNTED document from remounting under the user;
    // before anything has been delivered there is nothing to protect, and
    // stashing the first document leaves the card blank forever (a lightbox
    // that opens straight into an edit session mounts frozen).
    if (contentFrozenRef.current && hasDeliveredRef.current) {
      // Latest wins: only the newest server state is worth applying once
      // the session ends.
      pendingRef.current = { fingerprint, original };
      return;
    }
    void applyVersion(original, fingerprint).catch(() => {
      // Keep existing content on error — don't go blank.
    });
  }, [original, optionsSalt, prepare, applyVersion]);

  // Unfreeze reconcile: apply a stashed version only if it still differs from
  // what the card already shows.
  useEffect(() => {
    contentFrozenRef.current = contentFrozen;
    if (contentFrozen) return;
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    if (pending.fingerprint === expectedFingerprintRef.current) return;
    void applyVersion(pending.original, pending.fingerprint).catch(() => {});
  }, [contentFrozen, applyVersion]);

  useEffect(
    () => () => {
      generationRef.current += 1;
    },
    [],
  );

  return { displayDoc };
}
